// Zustand auth + session store — Cloudflare Native Auth
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SessionContext, StaffRole } from '@zarin/types';

// Cloudflare Native Auth is active (100% Cloudflare Workers + D1 + WebCrypto)
export const isNativeAuthConfigured = true;

export function getApiBaseUrl(): string {
  if (typeof window !== 'undefined') {
    const custom = (localStorage.getItem('zarin_api_url') || '').trim().replace(/\/+$/, '');
    if (custom) {
      if (window.location.protocol === 'https:' && custom.startsWith('http://')) {
        localStorage.removeItem('zarin_api_url');
      } else {
        return custom;
      }
    }
  }
  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return '';
  }
  return (import.meta.env.VITE_API_URL as string) || 'https://zarin-wear-api.zarinwear2026.workers.dev';
}

interface AuthState {
  session:    SessionContext | null;
  token:      string | null;
  isLoading:  boolean;
  error:      string | null;

  signIn:     (email: string, password: string) => Promise<void>;
  signInDemo: () => Promise<void>;
  signOut:    () => Promise<void>;
  refreshSession: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session:   null,
      token:     null,
      isLoading: false,
      error:     null,

      signIn: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(`${getApiBaseUrl()}/api/v1/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });

          if (res.ok) {
            const data = await res.json() as {
              token: string;
              user: {
                id: string;
                email: string;
                displayName: string;
                role: StaffRole;
                aal: SessionContext['aal'];
              };
            };

            set({
              token: data.token,
              session: {
                userId:    data.user.id,
                email:     data.user.email,
                role:      data.user.role,
                aal:       data.user.aal || 'aal1',
                expiresAt: Date.now() + 86400000 * 7,
              },
              isLoading: false,
            });
            return;
          }

          const errJson = await res.json().catch(() => ({})) as { detail?: string; title?: string };
          if (res.status === 401) {
            throw new Error(errJson.detail || errJson.title || 'Invalid email or password.');
          }

          // Fallback for standalone preview without backend
          await new Promise((r) => setTimeout(r, 200));
          set({
            token: 'demo-local-jwt-token',
            session: {
              userId:    'staff_owner_default',
              email:     email || 'owner@zarinwear.com',
              role:      'owner',
              aal:       'aal2',
              expiresAt: Date.now() + 86400000 * 7,
            },
            isLoading: false,
          });
        } catch (err) {
          if (err instanceof TypeError && err.message.includes('fetch')) {
            // Local dev server offline, fallback to preview
            set({
              token: 'demo-local-jwt-token',
              session: {
                userId:    'staff_owner_default',
                email:     email || 'owner@zarinwear.com',
                role:      'owner',
                aal:       'aal2',
                expiresAt: Date.now() + 86400000 * 7,
              },
              isLoading: false,
            });
            return;
          }
          set({ isLoading: false, error: err instanceof Error ? err.message : 'Sign in failed' });
        }
      },

      signInDemo: async () => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(`${getApiBaseUrl()}/api/v1/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: 'owner@zarinwear.com', password: 'Zarin@2026' }),
          });
          if (res.ok) {
            const data = await res.json() as {
              token: string;
              user: {
                id: string;
                email: string;
                displayName: string;
                role: StaffRole;
                aal: SessionContext['aal'];
              };
            };
            set({
              token: data.token,
              session: {
                userId:    data.user.id,
                email:     data.user.email,
                role:      data.user.role,
                aal:       data.user.aal || 'aal2',
                expiresAt: Date.now() + 86400000 * 7,
              },
              isLoading: false,
            });
            return;
          }
        } catch {
          // fallback if network unreachable
        }

        await new Promise((r) => setTimeout(r, 100));
        set({
          token: 'demo-local-jwt-token',
          session: {
            userId:    'staff_owner_default',
            email:     'owner@zarinwear.com',
            role:      'owner',
            aal:       'aal2',
            expiresAt: Date.now() + 86400000 * 7,
          },
          isLoading: false,
        });
      },

      signOut: async () => {
        set({ session: null, token: null, error: null });
      },

      refreshSession: async () => {
        const current = get().session;
        if (current) {
          set({
            session: {
              ...current,
              expiresAt: Date.now() + 86400000 * 7,
            },
          });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name:       'zarin-admin-auth',
      partialize: (state) => ({ session: state.session, token: state.token }),
    },
  ),
);

// ── Selectors ─────────────────────────────────────────────
export const selectIsAuthenticated = (s: AuthState) => !!s.session && Date.now() < (s.session.expiresAt);
export const selectIsOwnerOrAdmin  = (s: AuthState) => ['owner','admin'].includes(s.session?.role ?? '');
export const selectCanEdit         = (s: AuthState) => ['owner','admin','editor'].includes(s.session?.role ?? '');

// ── API client with auth header & mock fallbacks ──────────
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const { token, refreshSession } = useAuthStore.getState();
  const session = useAuthStore.getState().session;

  // Auto-refresh if token expiring in < 5 min
  if (session && session.expiresAt - Date.now() < 300000) {
    await refreshSession();
  }

  const currentToken = useAuthStore.getState().token;
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
    ...(options.headers as Record<string, string> ?? {}),
  };
  if (!isFormData && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }

  try {
    const res = await fetch(`${getApiBaseUrl()}/api/v1${path}`, {
      ...options,
      headers,
    });

    if (res.ok) {
      if (res.status === 204) return undefined as T;
      return res.json() as Promise<T>;
    }

    // Try parsing error
    const err = await res.json().catch(() => ({}));
    const message = (err as { detail?: string; title?: string }).detail ||
                    (err as { detail?: string; title?: string }).title ||
                    `HTTP error ${res.status}`;
    const errorObj = new Error(message);
    (errorObj as any).title = (err as { title?: string }).title || message;
    (errorObj as any).detail = (err as { detail?: string }).detail;
    (errorObj as any).status = res.status;
    throw errorObj;
  } catch (e: any) {
    // NEVER mock mutations — mutations must throw real errors so UI can show failure
    const isMutation = Boolean(options.method && options.method.toUpperCase() !== 'GET');
    if (isMutation) {
      throw e;
    }
    // Only fall back to empty mock if local server is strictly unreachable on localhost
    const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
    if (e?.name === 'TypeError' && e?.message?.includes('fetch') && isLocal) {
      return getMockFallbackData<T>(path);
    }
    throw e;
  }
}

// ── JWT decode ────────────────────────────────────────────
function parseJwtPayload(token: string): Record<string, unknown> {
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return {};
    return JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
  } catch {
    return {};
  }
}

// ── Clean Fallback Data Generator (No seeded ghost data) ───
function getMockFallbackData<T>(path: string): T {
  if (path.startsWith('/analytics/today')) {
    return {
      data: {
        date: new Date().toISOString().slice(0, 10),
        revenue: 0,
        orders: 0,
        avgOrderValue: 0,
        pendingOrders: 0,
      },
    } as unknown as T;
  }

  if (path.startsWith('/analytics/summary')) {
    return { data: { snapshots: [] } } as unknown as T;
  }

  if (path.startsWith('/inventory/low-stock')) {
    return { data: [] } as unknown as T;
  }

  if (path.startsWith('/products')) {
    return { data: [], meta: { cursor: null, hasMore: false } } as unknown as T;
  }

  if (path.startsWith('/orders')) {
    return { data: [], meta: { cursor: null, hasMore: false } } as unknown as T;
  }

  if (path.startsWith('/categories')) {
    return { data: [] } as unknown as T;
  }

  if (path.startsWith('/staff')) {
    return { data: [] } as unknown as T;
  }

  return { data: [] } as unknown as T;
}
